import { Banner, Spinner } from '../components/admin-ui';
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import "../adminWorkspace.css";
import AccountMenu from "../componens/accountMenu";
import storageUtils from "../utils/storageUtils";
import Dashboard from "./dashboard";
import ErrorLog from "./errorLog";
import ShopLinks from "./shopLinks";
import AdminAccounts from "./adminAccounts";
import StorePerformance from "./storePerformance";
import StorePerformanceDetail from "./storePerformanceDetail";
import CodRiskData from "./codRiskData";
import { admin_context } from "../api";

const allPermissions = [
  "shops.view", "shops.manage", "performance.view", "performance.export",
  "logs.view", "logs.delete", "cod_risk.import", "accounts.manage",
];

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [context, setContext] = useState(null);
  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    if (!storageUtils.get("NASadminToken")) {
      navigate("/login", { replace: true }); return;
    }
    let active = true;
    admin_context({}).then((response) => {
      if (active && response.status === 200) setContext(response.data);
      else if (active) setLoadError(true);
    }).catch(() => active && setLoadError(true));
    return () => { active = false; };
  }, [navigate]);
  useEffect(() => {
    const onStorage = (event) => {
      if ((event.key === "NASadminToken" || event.key === null) && !storageUtils.get("NASadminToken")) {
        navigate("/login", { replace: true });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [navigate]);

  const permissions = useMemo(() => context?.permissions || [], [context?.permissions]);
  const isOwner = Boolean(context?.is_owner);
  const can = useCallback((permission) => isOwner || permissions.includes(permission), [isOwner, permissions]);
  const tabs = useMemo(() => [
    { id: "Dashboard", content: "Shop settings", allowed: can("shops.view") },
    { id: "Performance", content: "Store performance", allowed: can("performance.view") },
    { id: "ErrorLog", content: "Error logs", allowed: can("logs.view") },
    { id: "Accounts", content: "Accounts & permissions", allowed: can("accounts.manage") },
    { id: "ShopLinks", content: "A/B shop links", allowed: isOwner },
    { id: "CodRiskData", content: "COD risk data", allowed: can("cod_risk.import") },
  ].filter((tab) => tab.allowed), [can, isOwner]);

  const activeTab = tabs.find((tab) => "/" + tab.id === location.pathname || (tab.id === "Performance" && location.pathname.startsWith("/Performance/")));
  useEffect(() => {
    if (!context) return;
    if (!activeTab) navigate(tabs.length ? `/${tabs[0].id}` : "/NoAccess", { replace: true });
  }, [activeTab, context, navigate, tabs]);

  if (!storageUtils.get("NASadminToken")) return <Navigate to="/login" replace />;
  if (!context && !loadError) return <Spinner accessibilityLabel="Loading administrator permissions" />;

  return (
    <div className="sa-shell">
      <header className="sa-shell-header">
        <div className="sa-brand"><span className="sa-brand-mark" aria-hidden="true" />NinaCheck<span>Super admin</span></div>
        <AccountMenu admin={context?.admin} isOwner={isOwner} />
      </header>
      <nav className="sa-navigation" aria-label="Super-admin navigation">{tabs.map((tab) => <button type="button" key={tab.id} aria-current={activeTab?.id === tab.id ? "page" : undefined} onClick={() => navigate(`/${tab.id}`)}>{tab.content}</button>)}</nav>
      {loadError ? <Banner tone="critical" title="Unable to load administrator permissions. Please sign in again." /> :
        !tabs.length ? <Banner title="No super-admin features are authorized for this account." /> : <Routes>
        <Route path="/" element={<Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/Dashboard" element={can("shops.view") ? <Dashboard canManage={can("shops.manage")} canCreate={Boolean(context.can_create_shops)} /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/Performance" element={can("performance.view") ? <StorePerformance /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/Performance/:shopId" element={can("performance.view") ? <StorePerformanceDetail /> : <Navigate to={"/" + tabs[0].id} replace />} />
        <Route path="/ErrorLog" element={can("logs.view") ? <ErrorLog canDelete={can("logs.delete")} /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/Accounts" element={can("accounts.manage") ? <AdminAccounts context={context} permissions={allPermissions} /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/ShopLinks" element={isOwner ? <ShopLinks /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/CodRiskData" element={can("cod_risk.import") ? <CodRiskData isOwner={isOwner} canImport={can("cod_risk.import")} /> : <Navigate to={`/${tabs[0].id}`} replace />} />
      </Routes>}
    </div>
  );
}
